import React, { useEffect, useState, Suspense } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuthStore } from "../../../store/authStore";
import { useSubscriptionStore } from "../../../store/subscriptionStore";
import { useToast } from "../../../hooks/useToast";
import { Card } from "../../Shared/Card";

const CurrentPlan = React.lazy(() => import("./CurrentPlan"));
const PaymentMethod = React.lazy(() => import("./PaymentMethod"));
const BillingHistory = React.lazy(() => import("./BillingHistory"));

const LoadingSkeleton = ({ className = "h-48" }: { className?: string }) => (
  <Card className="p-0 overflow-hidden">
    <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
      <div className="flex items-center justify-between">
        <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
        <div className="h-6 bg-gray-200 rounded w-16 animate-pulse"></div>
      </div>
    </div>
    <div className="p-4 space-y-3">
      <div className="h-3 bg-gray-200 rounded animate-pulse"></div>
      <div className="h-3 bg-gray-200 rounded w-3/4 animate-pulse"></div>
      <div className="h-3 bg-gray-200 rounded w-1/2 animate-pulse"></div>
    </div>
  </Card>
);

const FullPageLoadingSkeleton = () => (
  <div className="space-y-8">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <LoadingSkeleton />
      <LoadingSkeleton />
    </div>
    <LoadingSkeleton className="h-96" />
  </div>
);

export const BillingView = () => {
  const [isLoading, setIsLoading] = useState(true);
  const user = useAuthStore((state) => state.user);
  const { checkSubscriptionStatus } = useSubscriptionStore();
  const { showToast } = useToast();
  const [searchParams] = useSearchParams();

  // Handle subscription success callback and refresh subscription status
  useEffect(() => {
    const handleSuccess = async () => {
      const success = searchParams.get("success");
      if (success === "true" && user?.id) {
        try {
          // Show immediate success feedback
          showToast("Processing subscription activation...", "success");

          // Dodo Payments handles checkout via webhooks, so we just need to refresh status
          await checkSubscriptionStatus(user.id);

          // Show final success message
          setTimeout(() => {
            showToast("Subscription activated successfully!", "success");
          }, 1000);

          // Remove success parameter from URL
          searchParams.delete("success");
          window.history.replaceState(
            {},
            "",
            `${window.location.pathname}?${searchParams.toString()}`
          );
        } catch (error) {
          console.error("Subscription status check error:", error);
          showToast("Failed to verify subscription status", "error");
        }
      }

      // Small delay to show loading state
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    };

    handleSuccess();
  }, [searchParams, user?.id]);

  if (isLoading) {
    return <FullPageLoadingSkeleton />;
  }

  return (
    <div className="p-4 space-y-4 max-w-6xl mx-auto">
      <div className="grid gap-4 md:grid-cols-2">
        {/* Current Plan */}
        <Suspense fallback={<LoadingSkeleton className="h-48" />}>
          <CurrentPlan />
        </Suspense>

        {/* Payment Method */}
        <Suspense fallback={<LoadingSkeleton className="h-48" />}>
          <PaymentMethod />
        </Suspense>
      </div>

      {/* Billing History */}
      <div className="col-span-full">
        <Suspense fallback={<LoadingSkeleton className="h-64" />}>
          <BillingHistory />
        </Suspense>
      </div>
    </div>
  );
};
